import {Text} from '@mantine/core';
import Image from 'next/image';
import {type FC, useState} from 'react';
import type {Recruitment} from '../../../../core/repositories/recruitingInfoRepository';
import {cn} from '../../../../lib/classname';
import {CommonLink} from '../../../../shared/components/CommonLink';
import {
  GradientEntryButton,
  QUICK_ENTRY_BUTTON,
} from '../../../../shared/components/EntryButton/GradientEntryButton';
import {appendQueryParam} from '../../../../shared/services/appendQueryParam';
import {Deadline} from '../../../recruitingInfo/components/Container/Presenter/Deadline';

type Props = {
  recruitment: Recruitment;
  isQuickEntry: boolean;
  componentId: string;
};

export const PostRegisterEntryPromptCard: FC<Props> = ({
  recruitment,
  isQuickEntry,
  componentId,
}) => {
  const company = recruitment.companies[0];
  const [showLogo, setShowLogo] = useState(
    Boolean(company?.logoURLs?.['140x105']),
  );

  const recruitingInfoPage = `/recruiting_info/${
    recruitment.category === 'archive' ? 'archives' : 'view'
  }/${recruitment.detailID}`;
  const entryButtonUrl = appendQueryParam(
    recruitment.entryURL,
    'source_url',
    recruitment.detailPageURL,
  );
  const schedule = recruitment.schedules[0];
  // 他の募集カードと表示条件を揃える
  const canShowEntryButton =
    recruitment.entryURL &&
    ['medium', 'large'].includes(company?.pageType ?? '');

  return (
    <div className="flex flex-col gap-8 rounded-12 border border-line-1 bg-white p-12">
      <div className="flex items-center gap-8">
        {showLogo && company?.logoURLs['140x105'] && (
          <Image
            className="h-[20px] w-[42px] flex-shrink-0 bg-white object-contain"
            src={company.logoURLs['140x105']}
            alt={company.name}
            width={42}
            height={20}
            onError={() => setShowLogo(false)}
          />
        )}
        <Text className="line-clamp-1 text-sm text-black-2">
          {company?.name}
        </Text>
      </div>

      <CommonLink
        href={recruitingInfoPage}
        data-gtm-click={`title-${componentId}`}
        data-gtm-click-recruitment-detail-button="true"
      >
        <Text className="line-clamp-2 text-base font-bold text-black-1 hover:opacity-50">
          {recruitment.title}
        </Text>
      </CommonLink>

      <div className="flex flex-col gap-8">
        {schedule && <Deadline schedule={schedule} layout="text" />}

        <div className="flex justify-end gap-12">
          <CommonLink
            href={recruitingInfoPage}
            data-gtm-click={`detail-${componentId}`}
            data-gtm-click-recruitment-detail-button="true"
            className={cn(
              'text-sm font-bold',
              'flex h-[36px] w-[83px] items-center justify-center rounded-[8px] hover:opacity-80',
              'border border-black-3 bg-white text-black-3',
            )}
          >
            募集詳細
          </CommonLink>
          {canShowEntryButton && (
            <GradientEntryButton
              entryUrl={entryButtonUrl}
              customId={`post_register_entry_button_${recruitment.detailID}`}
              withShadow
              dataGtmClick={`entry-${componentId}`}
              companyId={company?.id}
              companyName={company?.name}
              entryTitle={recruitment.title}
              deadline={schedule?.entryEnd}
              label={isQuickEntry ? QUICK_ENTRY_BUTTON.label : undefined}
              tooltip={isQuickEntry ? QUICK_ENTRY_BUTTON.tooltip : undefined}
              gradientClassName={
                isQuickEntry ? QUICK_ENTRY_BUTTON.gradientClassName : undefined
              }
              // ラベルの長さでボタン幅が変わると、カードごとにボタンの位置がずれるため揃える
              className="h-[36px] w-[148px]"
            />
          )}
        </div>
      </div>
    </div>
  );
};
